import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import jwt_decode from "jwt-decode";
import { addAuthInfo } from '../slices/auth';
import { logger } from '../Util/Logger/ClientLogger';
import { scheduleProactiveAccessTokenRefresh } from '../Services/instances/VoiceIQAPI';
import { isUsableAuthFromApex } from '../helpers/apiHelper';

import { useExternalScript } from "./useExternalScript";

export const useMetaDataLoad = () => {
  const [errorCode, setErrorCode] = useState<number>(0);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(true);


  const dispatch = useDispatch();
  let orgBaseUrl = null;
  
  if (window?.location?.ancestorOrigins) {
    orgBaseUrl = window?.location?.ancestorOrigins[0];
  } else if (window.location.search) {
    let urlParams = new  URLSearchParams(window.location.search);
    let paramsObj = {};
    // @ts-ignore
    urlParams?.forEach((v,k)=> paramsObj[k] = v);
    // @ts-ignore
    orgBaseUrl = paramsObj?.sfdcIframeOrigin;
  } else {
    orgBaseUrl = null;
  }

  const openCTIState = useExternalScript(orgBaseUrl ? orgBaseUrl + '/support/api/58.0/lightning/opencti_min.js' : null);
  const integrationState = useExternalScript(orgBaseUrl ? orgBaseUrl + '/support/console/58.0/integration.js' : null);


  const saveAuthInfo = useCallback((authInfo: any) => {
    const token = authInfo?.AccessToken;
    let decoded: any;
    try {
      decoded = jwt_decode(token);
    } catch {
      setFetched(false);
      setLoading(false);
      setErrorCode(401);
      return;
    }
    const userId = decoded.uamUserId || decoded.sub;
    const tenantCode = decoded.tenantCode || decoded.website;
    let rtaspUrl: string | undefined = '';

    if (authInfo?.RPAPIGW_URL === process.env.REACT_APP_PROD_APIGW_URL) {
      rtaspUrl = process.env.REACT_APP_PROD_RTASP_URL;
    } else if (authInfo?.RPAPIGW_URL === process.env.REACT_APP_STAGING_APIGW_URL) {
      rtaspUrl = process.env.REACT_APP_STAGING_RTASP_URL;
    }  else if (authInfo?.RPAPIGW_URL === process.env.REACT_APP_QA_APIGW_URL) {
      rtaspUrl = process.env.REACT_APP_QA_RTASP_URL;
    } else {
      rtaspUrl = process.env.REACT_APP_DEV_RTASP_URL;
    }

    logger.setUserContext({
      userId: userId,
      tenantId: tenantCode,
      baseUrl: authInfo?.RPAPIGW_URL,
      token: token
    });

    const processedAuthData = { ...authInfo, userId, tenantCode, rtaspUrl };

    dispatch(addAuthInfo(processedAuthData));
    scheduleProactiveAccessTokenRefresh();
  },[dispatch]);

  useEffect(() => {
    if (openCTIState === 'error') {
      setErrorCode(100);
    } else {
      if (openCTIState === 'ready' && integrationState === 'ready') {

        // @ts-ignore
        (sforce as any)?.console.setCustomConsoleComponentPopoutable(false);

        // @ts-ignore
        sforce.opencti.runApex({
          apexClass: 'rocketphone.RPGetLoggedInUserInfo',
          methodName: 'getUserInfo',
          methodParams: '',
          callback: (response: any) => {
            if (response.success) {
              let authInfo: AuthInfo;
              try {
                authInfo = JSON.parse(response.returnValue.runApex) as AuthInfo;
              } catch {
                setFetched(false);
                setLoading(false);
                setErrorCode(401);
                return;
              }
              if (!isUsableAuthFromApex(authInfo)) {
                setFetched(false);
                setLoading(false);
                setErrorCode(401);
                return;
              }
              saveAuthInfo(authInfo);
              setErrorCode(0);
              setLoading(false);
              setFetched(true);
            } else {
              setFetched(false);
              setLoading(false);
              setErrorCode(401);
            }
          }
        });
     }
    }
  }, [openCTIState, integrationState, saveAuthInfo]);
   
  return [errorCode, loading, fetched];
};

export type AuthInfo = {
  UserId: string;
  Username: string;
  Email: string;
  FullName: string,
  OrgId: string,
  OrgName: string,
  RPAPIGW_URL: string,
  StatusCode: string,
  Status: number,
  AccessToken: string,
  RefreshToken: string,
  ExpiresIn: number,
  TokenType: string,
};